import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowRight } from 'lucide-react';
import heroImage from '@/assets/pottery-wheel-hands.jpg';
import ceramicVessel from '@/assets/organic-vessels-outdoor.jpg';
import ceramicBowl from '@/assets/luna-terra-collection.jpg';
import ceramicSculpture from '@/assets/wildroot-vessel.jpg';
import artistWheelStudio from '@/assets/artist-wheel-studio.jpg';

const Home = () => {
  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] sm:min-h-screen flex items-center hero-gradient overflow-hidden">
        <img 
          src={heroImage}
          alt="Pottery wheel crafting at Terravia Ceramics studio"
          className="absolute inset-0 w-full h-full object-cover opacity-10 mix-blend-multiply"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-transparent to-background"></div>
        
        <div className="container mx-auto container-padding relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="animate-fade-in">
              <p className="text-sm sm:text-base uppercase tracking-[0.3em] text-muted-foreground mb-6 sm:mb-8 animate-fade-in">
                Contemporary Ceramic Art
              </p>
              <h1 className="font-display text-hero text-foreground mb-6 sm:mb-8 animate-fade-in">
                <span className="block">Terravia</span>
                <span className="block text-clay-rich">Ceramics</span>
              </h1>
              <p className="text-body-large md:text-display font-light text-foreground/80 mb-10 sm:mb-14 max-w-2xl mx-auto animate-slide-in-right" style={{ animationDelay: '0.3s' }}>
                Contemporary pottery and ceramic art shaped by earth and fire.
              </p>
              <div className="max-w-2xl mx-auto mb-12 sm:mb-16">
                <p className="text-body text-foreground/70 leading-relaxed font-light animate-fade-in" style={{ animationDelay: '0.5s' }}>
                  Terravia Ceramics, founded by Umar Abdulrasheed, is a studio dedicated to creating vessels and sculptural works that embody the timeless dialogue between clay, fire, and form.
                </p>
              </div>
              <div className="animate-scale-in flex flex-col sm:flex-row gap-4 justify-center" style={{ animationDelay: '0.7s' }}>
                <Link to="/portfolio">
                  <Button 
                    size="lg" 
                    className="btn-luxury px-8 sm:px-10 py-5 sm:py-6 text-sm sm:text-base font-medium tracking-wide rounded-full"
                  >
                    Explore Portfolio
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="px-8 sm:px-10 py-5 sm:py-6 text-sm sm:text-base font-medium tracking-wide rounded-full border-border hover:bg-muted"
                  >
                    About the Artist
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Works Preview */}
      <section className="section-padding bg-background">
        <div className="container mx-auto container-padding">
          <div className="text-center mb-16 sm:mb-24">
            <p className="text-sm uppercase tracking-[0.25em] text-muted-foreground mb-4 animate-fade-in">
              Selected Works
            </p>
            <h2 className="font-display text-display text-foreground mb-6 sm:mb-8 animate-fade-in">
              Featured Works
            </h2>
            <p className="text-body-large text-muted-foreground max-w-2xl mx-auto font-light animate-slide-in-right" style={{ animationDelay: '0.2s' }}>
              Each piece tells a story of transformation, where earth meets fire to create lasting beauty.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-7xl mx-auto">
            <Card className="hover-lift bg-card border-border/40 shadow-soft group overflow-hidden rounded-2xl animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="image-hover rounded-t-2xl">
                <img 
                  src={ceramicVessel} 
                  alt="Organic earth-toned ceramic vessels by Terravia Ceramics"
                  className="w-full h-72 sm:h-80 object-cover"
                  width={380}
                  height={320}
                  loading="lazy"
                />
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="font-display text-title mb-2 text-foreground group-hover:text-clay-rich transition-luxury">
                  Organic Vessels
                </h3>
                <p className="text-muted-foreground text-body-small">Handcrafted stoneware · Natural earth tones</p>
              </div>
            </Card>

            <Card className="hover-lift bg-card border-border/40 shadow-soft group overflow-hidden rounded-2xl animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="image-hover rounded-t-2xl">
                <img 
                  src={ceramicBowl} 
                  alt="Luna Terra Collection contemporary ceramic forms"
                  className="w-full h-72 sm:h-80 object-cover"
                  width={380}
                  height={320}
                  loading="lazy"
                />
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="font-display text-title mb-2 text-foreground group-hover:text-clay-rich transition-luxury">
                  Luna Terra Collection
                </h3>
                <p className="text-muted-foreground text-body-small">Contemporary sculptural forms · Mixed media with wood</p>
              </div>
            </Card>

            <Card className="hover-lift bg-card border-border/40 shadow-soft group overflow-hidden rounded-2xl animate-fade-in" style={{ animationDelay: '0.5s' }}>
              <div className="image-hover rounded-t-2xl">
                <img 
                  src={ceramicSculpture} 
                  alt="Wildroot vessel in deep teal glaze"
                  className="w-full h-72 sm:h-80 object-cover"
                  width={380}
                  height={320}
                  loading="lazy"
                />
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="font-display text-title mb-2 text-foreground group-hover:text-clay-rich transition-luxury">
                  Wildroot Vessel
                </h3>
                <p className="text-muted-foreground text-body-small">Wheel-thrown stoneware · Deep teal matte glaze</p>
              </div>
            </Card>
          </div>

          <div className="text-center mt-14 sm:mt-20 animate-scale-in" style={{ animationDelay: '0.7s' }}>
            <Link to="/portfolio">
              <Button variant="outline" size="lg" className="px-10 py-5 text-sm font-medium tracking-wide rounded-full border-border hover:bg-muted transition-luxury">
                View Complete Portfolio
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Artist Introduction */}
      <section className="section-padding section-gradient relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03] bg-gradient-to-br from-foreground via-transparent to-transparent"></div>
        
        <div className="container mx-auto container-padding relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center max-w-7xl mx-auto">
            <div className="order-2 lg:order-1 animate-fade-in">
              <p className="text-sm uppercase tracking-[0.25em] text-muted-foreground mb-4">
                About the Artist
              </p>
              <h2 className="font-display text-display text-foreground mb-8 sm:mb-10">
                Umar Abdulrasheed
              </h2>
              <div className="content-spacing">
                <p className="text-body-large text-foreground/80 leading-relaxed font-light">
                  At Terravia, my practice explores the relationship between tradition and innovation in ceramics. Working with stoneware and porcelain, I create forms that balance function with sculptural presence.
                </p>
                <p className="text-body text-foreground/70 leading-relaxed font-light italic border-l-2 border-clay-warm/50 pl-6">
                  "Clay is more than material to me — it is living earth, shaped by touch and memory, transformed through fire into lasting vessels of expression."
                </p>
                <div className="pt-4">
                  <Link to="/about">
                    <Button className="btn-luxury px-8 py-5 text-sm font-medium tracking-wide rounded-full">
                      Learn More About My Journey
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2 animate-slide-in-right" style={{ animationDelay: '0.3s' }}>
              <div className="overflow-hidden rounded-2xl shadow-strong hover-glow">
                <img 
                  src={artistWheelStudio} 
                  alt="Artist working with clay at the pottery wheel"
                  className="w-full aspect-[3/4] sm:aspect-[4/5] object-cover object-center"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
